/**
 * Spec 76 Phase A — URL state for the ContextPanel framework.
 *
 * Single source of truth: `?panel=<type>:<id>`.
 *
 * Parsing splits on the FIRST colon only, so ids may themselves contain
 * colons in future (e.g. compound keys, URNs).
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPanelType } from './registry.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PanelPointer {
  type: string;
  id: string;
}

// ─── Pure helpers (no React) ──────────────────────────────────────────────────

/**
 * Parse the `?panel=` search-param value into a { type, id } pointer.
 *
 * Splits on the **first** colon only — ids may contain colons.
 * Returns `null` for empty, undefined, or malformed (no colon / empty
 * type or id) values.
 */
export function parsePanelUrl(value: string | null | undefined): PanelPointer | null {
  if (!value) return null;

  const colonIndex = value.indexOf(':');
  if (colonIndex === -1) return null;

  const type = value.slice(0, colonIndex);
  const id = value.slice(colonIndex + 1);

  if (!type || !id) return null;

  return { type, id };
}

/**
 * Serialize a { type, id } pointer back into the `?panel=` string value.
 * Returns `null` when the pointer is null (meaning: remove the param).
 */
export function serializePanelUrl(panel: PanelPointer | null): string | null {
  if (!panel) return null;
  return `${panel.type}:${panel.id}`;
}

// ─── React hook ───────────────────────────────────────────────────────────────

export interface UseContextPanelReturn {
  /** The currently-open panel, or null if none is open. */
  panel: PanelPointer | null;
  /** Open a panel by navigating to `?panel=<type>:<id>`. Preserves all other params. */
  openPanel: (pointer: PanelPointer) => void;
  /** Close the panel by removing `?panel=` from the URL. Preserves all other params. */
  closePanel: () => void;
}

/**
 * Hook that reads / writes the `?panel=` search param via React Router v6.
 *
 * Must be used inside a `<BrowserRouter>` (or any Router that provides
 * `useSearchParams`). The hook preserves all other search params when
 * opening or closing.
 */
export function useContextPanel(): UseContextPanelReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const panel = parsePanelUrl(searchParams.get('panel'));

  const openPanel = useCallback(
    (pointer: PanelPointer) => {
      // Defense in depth: only allow opening panel types registered with the framework.
      // Caller-supplied pointers (e.g., from orchestrator tool output) cannot route to
      // arbitrary or unregistered types. The panel component itself is still responsible
      // for its own authorization (`<Can policy="...">`).
      if (getPanelType(pointer.type) === null) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const serialized = serializePanelUrl(pointer);
          if (serialized !== null) {
            next.set('panel', serialized);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closePanel = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('panel');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return { panel, openPanel, closePanel };
}
